import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import { and } from 'ember-truth-helpers';
import { sourceToAbilityMap } from 'garaje/abilities/entry-approval';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import dateFormat from 'garaje/helpers/date-format';
import Entry from 'garaje/models/entry';
import type ScreenerResult from 'garaje/models/screener-result';
import type { IdScanningReport } from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';

interface VisitApprovalResultResolutionSignature {
  Args: {
    model: GuestWithVisitApproval;
    isExpired: boolean;
  };
}

function getSystemResolutionTemplate(result: ScreenerResult) {
  if (result.isReview) return <template></template>;
  return <template>
    <div class="text-sm my-2" data-test-access-message>
      Access was
      {{if result.isDenied "denied" "approved"}}
      by system.
    </div>
  </template>;
}

/**
 * This component serves to surface screener messages for invites and entries that have been approved but with no
 * review history. This is useful for cases where the screener results were not flagged for review but the
 * user still needs to be informed about the outcome.
 */
export default class VisitApprovalResultResolution extends Component<VisitApprovalResultResolutionSignature> {
  @service declare abilities: AbilitiesService;

  get entry(): Entry | undefined {
    return this.args.model instanceof Entry ? this.args.model : undefined;
  }

  /**
   * results that have not been reviewed as they were not flagged for review but have had an action flagged by a previous review
   */
  get visibleResults(): ScreenerResult[] {
    return this.args.model.visitApproval.screenerResults.filter((result) => {
      const canViewOrReview = this.checkCanView(result) || this.checkCanReview(result);

      if (result.isReview && this.args.isExpired && canViewOrReview) return true;
      return (result.isApproved || result.isPending || result.isDenied) && !result.reviewedAt && canViewOrReview;
    });
  }

  get blocklistResult(): ScreenerResult | undefined {
    return this.visibleResults.find((result) => result.isBlocklist());
  }

  get idScanningResult(): ScreenerResult<IdScanningReport> | undefined {
    return this.visibleResults.find((result) => result.isIdScanning());
  }

  get preRegistrationRequiredResult(): ScreenerResult | undefined {
    return this.visibleResults.find((result) => result.isPreRegistrationRequired());
  }

  get visualComplianceResult(): ScreenerResult | undefined {
    return this.visibleResults.find((result) => result.isVisualCompliance());
  }

  checkCanReview(review: ScreenerResult): boolean {
    const source = sourceToAbilityMap[review.screenerSource] || review.screenerSource;
    return this.abilities.can(`review ${source}`, this.#permissionsModel);
  }

  checkCanView(review: ScreenerResult): boolean {
    const source = sourceToAbilityMap[review.screenerSource] || review.screenerSource;

    if (review.isBlocklist()) return this.abilities.can(`read blocklist for security`, this.#permissionsModel);
    return this.abilities.can(`view ${source}`, this.#permissionsModel);
  }

  get #permissionsModel(): GetCanReviewOrReadBlocklistMatchModel {
    return {
      context: 'location',
      parent: this.args.model,
      visitApproval: this.args.model.visitApproval,
    };
  }

  <template>
    <div data-test-approval-resolution>
      {{#if (and this.blocklistResult)}}
        <div data-test-blocklist-history class="border-b mb-8">
          <div class="label mb-2">Block list</div>
          {{#if this.blocklistResult.didFail}}
            <div class="text-sm text-carbon-70">{{@model.fullName}} matched someone on your blocklist.</div>
            {{#let (getSystemResolutionTemplate this.blocklistResult) as |ResolutionTemplate|}}
              <ResolutionTemplate />
            {{/let}}
          {{else}}
            <p class="text-sm">No match was found on your block list</p>
          {{/if}}
        </div>
      {{/if}}
      {{#if this.idScanningResult}}
        <div data-test-id-scanned class="border-b mb-8">
          <div class="label mb-2">Id Scan</div>
          {{#if this.idScanningResult.didFail}}
            <p class="text-sm" data-test-id-scanned-invalid>{{this.idScanningResult.idScanningFailureMessage}}</p>
            {{#let (getSystemResolutionTemplate this.idScanningResult) as |ResolutionTemplate|}}
              <ResolutionTemplate />
            {{/let}}
          {{else if this.idScanningResult.isPending}}
            <p class="text-sm" data-test-id-scanning-pending>
              <img src="/assets/images/progress-small-gray.svg" alt="" role="presentation" />&nbsp;
              <span class="whitespace-nowrap">
                Visitor’s ID is processing.
              </span>
            </p>
          {{else}}
            <p class="text-sm" data-test-id-scanned-valid>ID accepted.</p>
          {{/if}}
        </div>
      {{/if}}

      {{#if this.preRegistrationRequiredResult}}
        <div data-test-pre-registration-required-history class="border-b mb-8">
          <div class="label mb-2">Pre-registration</div>
          {{#if this.preRegistrationRequiredResult.didFail}}
            <p class="text-sm">Visitor was not invited.</p>
            {{#let (getSystemResolutionTemplate this.preRegistrationRequiredResult) as |ResolutionTemplate|}}
              <ResolutionTemplate />
            {{/let}}
          {{else if this.entry.invite.content.id}}
            <p data-test-pre-registration-details class="text-sm mb-0">
              Visitor was invited by
              {{this.entry.invite.content.creator.content.fullName}}.
            </p>
            <p class="text-xs text-carbon-50 lining-nums">
              {{#if this.entry.invite.content.createdAt}}
                <span data-test-invite-created-at>{{dateFormat this.entry.invite.content.createdAt "PP 'at' p"}}</span>
              {{/if}}
            </p>
          {{else}}
            <p data-test-pre-registration-details class="text-sm mb-0">Visitor was invited</p>
            <p class="text-xs text-carbon-50">Invite details unavailable</p>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
